import React, { useState, useEffect, useMemo } from "react";
import { Button } from "@mui/material";
import { CONFIGS } from "../config/appConfig";
import CaptionText from "./CaptionText";
import ImageWithBoxes from "./ImageWithBoxes";
import TextualExplanation from "./TextualExplanation";
import VisualTextualExplanation from "./VisualTextualExplanation";
import "./Tutorial.css";
import "./forms/AfterImageQuestionnaire.css";
import { TUTORIAL_INTRO_PEOPLE } from "../config/introPeople";
import {
  getExplanationEntryDedupKey,
  getTextualExplanationEntries,
  getVisualExplanationEntries,
} from "../utils/explanationUtils";
import { getOptimizedImageUrl } from "../utils/imagePaths";

/**
 * Tutorial component shown before the main experiment.
 * - Page 0: Study context — Marie & James story
 * - Page 1: Caption quality scale explanation
 * - Page 2: How to interact (condition-appropriate, without naming condition)
 * - Pages 3-5: 3 practice images loaded from sample.json
 * - Page 6: Ready to start
 */

// Single source of truth for the 5-point caption-quality scale. Wording matches
// the real study (AfterImageQuestionnaire) so the tutorial and the actual rating
// cards read identically.
const RATING_SCALE = [
  { rating: 1, value: "1", label: "Very Poor", color: "#dc2626", desc: "Mostly wrong, or describes a different situation from what is shown." },
  { rating: 2, value: "2", label: "Poor", color: "#ea580c", desc: "Noticeable errors or missing information. Could mislead the photobook reader." },
  { rating: 3, value: "3", label: "Fair", color: "#ca8a04", desc: "Captures the basic idea, but incomplete, too general, or missing context." },
  { rating: 4, value: "4", label: "Good", color: "#65a30d", desc: "Mostly correct and useful, but misses some detail or is a little vague." },
  { rating: 5, value: "5", label: "Excellent", color: "#15803d", desc: "Main people, objects, scene, and actions are correct. Specific, well located, and useful." },
];

function buildPracticeExplanation(image, hoveredId, hoveredTokenId) {
  if (!image || (!hoveredId && hoveredTokenId == null)) {
    return null;
  }

  const segment = (image.segments || []).find((item) => item?.id === hoveredId);
  const tokenIds = new Set(segment?.token_ids || []);
  if (hoveredTokenId != null) {
    tokenIds.add(hoveredTokenId);
  }
  if (!segment && tokenIds.size === 0) {
    return null;
  }

  const dedup = (entries) => {
    const seen = new Set();
    return entries.filter((entry) => {
      const key = getExplanationEntryDedupKey(entry);
      if (seen.has(key)) return false;
      seen.add(key);
      return true;
    });
  };

  const matchesHoverTarget = (entry) => {
    if (hoveredTokenId != null) {
      return entry?.token_index === hoveredTokenId;
    }
    return tokenIds.has(entry?.token_index) || (segment?.id && entry?.segment_id === segment.id);
  };

  const visual = dedup(getVisualExplanationEntries(image).filter(matchesHoverTarget));
  // Keep every matching textual entry (incl. faces) — the text-only practice
  // view has no visual panel, so faces must not be filtered out here.
  const textual = dedup(getTextualExplanationEntries(image).filter(matchesHoverTarget));

  return { segment: segment || null, textual, visual, tokenId: hoveredTokenId };
}

export default function Tutorial({ assignedMode, onComplete }) {
  const [page, setPage] = useState(0);
  const [practiceRating, setPracticeRating] = useState("");
  const [showFeedback, setShowFeedback] = useState(false);
  const [samples, setSamples] = useState([]);
  const [hoveredId, setHoveredId] = useState(null);
  const [hoveredTokenId, setHoveredTokenId] = useState(null);
  const [pinnedTarget, setPinnedTarget] = useState(null);

  useEffect(() => {
    Promise.all([
      fetch(`${process.env.PUBLIC_URL}/sample.json`).then((r) => r.json()),
      fetch(`${process.env.PUBLIC_URL}/data_with_method_visual_explanations.json`)
        .catch(() => fetch(`${process.env.PUBLIC_URL}/data.json`))
        .then((r) => r.json()),
    ])
      .then(([sampleRows, studyRows]) => {
        const byImageUrl = new Map((Array.isArray(studyRows) ? studyRows : []).map((row) => [row.imageUrl, row]));
        setSamples(
          (Array.isArray(sampleRows) ? sampleRows : []).map((sample) => {
            const richEntry = byImageUrl.get(sample.sourceImageUrl || sample.imageUrl) || {};
            return {
              ...richEntry,
              ...sample,
              tokens: sample.tokens || richEntry.tokens || [],
              segments: sample.segments || richEntry.segments || [],
              factsExplanation: sample.factsExplanation || sample.facts || richEntry.factsExplanation || [],
              visualExplanation: sample.visualExplanation || richEntry.visualExplanation || [],
            };
          })
        );
      })
      .catch((err) => console.error("Failed to load sample.json:", err));
  }, []);

  const practiceIndex = page - 3; // Pages 3,4,5 → practice 0,1,2
  const currentPractice = samples[practiceIndex] || null;
  const totalPages = 7;

  useEffect(() => {
    setHoveredId(null);
    setHoveredTokenId(null);
    setPinnedTarget(null);
  }, [currentPractice?.imageUrl, assignedMode]);

  const handleRateSubmit = () => {
    if (!practiceRating) return;
    setShowFeedback(true);
  };

  const acceptedRatingsFor = (expectedRating) => {
    if (expectedRating === "1") return ["1", "2"];
    if (expectedRating === "3") return ["2", "3", "4"];
    if (expectedRating === "5") return ["4", "5"];
    const rating = Number(expectedRating);
    return [String(rating - 1), String(rating), String(rating + 1)].filter(
      (value) => Number(value) >= 1 && Number(value) <= 5
    );
  };

  const ratingAccepted = currentPractice
    ? acceptedRatingsFor(currentPractice.expectedRating).includes(practiceRating)
    : false;
  const handleNextFromFeedback = () => {
    setPracticeRating("");
    setShowFeedback(false);
    setPage((p) => p + 1);
  };

  // Determine which explanation features are available for this mode
  const hasVisual =
    assignedMode === CONFIGS.MODE_2 ||
    assignedMode === CONFIGS.MODE_4 ||
    assignedMode === CONFIGS.MODE_5;
  const hasTextual =
    assignedMode === CONFIGS.MODE_3 || assignedMode === CONFIGS.MODE_5;
  const hasSegmentHighlight = assignedMode !== CONFIGS.MODE_1;
  const isNoExplanation = assignedMode === CONFIGS.MODE_1;

  const practiceExplanation = useMemo(
    () => buildPracticeExplanation(currentPractice, hoveredId, hoveredTokenId),
    [currentPractice, hoveredId, hoveredTokenId]
  );

  const handlePracticeCaptionHover = (hoverTarget) => {
    if (!hoverTarget) {
      setHoveredId(pinnedTarget?.segmentId || null);
      setHoveredTokenId(pinnedTarget?.tokenId ?? null);
      return;
    }

    const segmentId = typeof hoverTarget === "object" && hoverTarget !== null ? hoverTarget.segmentId || null : hoverTarget;
    const tokenId = typeof hoverTarget === "object" && hoverTarget !== null ? hoverTarget.tokenId ?? null : null;
    setHoveredId(segmentId);
    setHoveredTokenId(tokenId);
  };

  const handlePracticeCaptionClick = (hoverTarget) => {
    const nextTarget = hoverTarget
      ? {
          segmentId: hoverTarget.segmentId || null,
          tokenId: hoverTarget.tokenId ?? null,
        }
      : null;
    const isSamePinned =
      (pinnedTarget?.segmentId || null) === (nextTarget?.segmentId || null) &&
      (pinnedTarget?.tokenId ?? null) === (nextTarget?.tokenId ?? null);
    const targetToPin = isSamePinned ? null : nextTarget;
    setPinnedTarget(targetToPin);
    setHoveredId(targetToPin?.segmentId || null);
    setHoveredTokenId(targetToPin?.tokenId ?? null);
  };

  const handlePracticeImageHover = (segmentId) => {
    setHoveredId(segmentId || pinnedTarget?.segmentId || null);
    setHoveredTokenId(segmentId ? null : pinnedTarget?.tokenId ?? null);
  };

  const handlePracticeImageClick = (segmentId) => {
    if (!segmentId) return;
    const nextTarget = { segmentId, tokenId: null };
    const isSamePinned =
      (pinnedTarget?.segmentId || null) === nextTarget.segmentId &&
      (pinnedTarget?.tokenId ?? null) === null;
    const targetToPin = isSamePinned ? null : nextTarget;
    setPinnedTarget(targetToPin);
    setHoveredId(targetToPin?.segmentId || null);
    setHoveredTokenId(null);
  };

  const renderPracticeExplanationPanel = () => {
    if (isNoExplanation || !currentPractice) {
      return null;
    }

    if (assignedMode === CONFIGS.MODE_3) {
      return (
        <TextualExplanation
          explanations={practiceExplanation?.textual}
          hoveredId={hoveredId}
          showMetadata={false}
        />
      );
    }

    if (assignedMode === CONFIGS.MODE_4 || assignedMode === CONFIGS.MODE_5) {
      return (
        <VisualTextualExplanation
          hoveredId={hoveredId}
          visualExplanations={practiceExplanation?.visual}
          textualExplanations={assignedMode === CONFIGS.MODE_5 ? practiceExplanation?.textual : null}
          showVisualReasoning={assignedMode === CONFIGS.MODE_5}
          requireVisualEvidence={assignedMode === CONFIGS.MODE_4}
        />
      );
    }

    return null;
  };

  const renderPracticeStudyPreview = () => {
    if (!currentPractice) return null;

    if (assignedMode === CONFIGS.MODE_1) {
      return (
        <div style={styles.practicePreviewSingle}>
          <ImageWithBoxes key={currentPractice.imageUrl} imageUrl={currentPractice.imageUrl} imageWidth={currentPractice.imageWidth} imageHeight={currentPractice.imageHeight} segments={[]} />
          <CaptionText tokens={currentPractice.tokens} entities={[]} requireSegmentForInteraction={true} />
        </div>
      );
    }

    const showSimpleVisual = assignedMode === CONFIGS.MODE_2;
    const showTextual = assignedMode === CONFIGS.MODE_3;
    const showVisual = assignedMode === CONFIGS.MODE_4;
    const showComplementary = assignedMode === CONFIGS.MODE_5;

    return (
      <div className="tutorial-practice-preview" style={styles.practiceStudyPreview}>
        <div style={styles.practicePreviewSingle}>
          <ImageWithBoxes
            key={currentPractice.imageUrl}
            imageUrl={currentPractice.imageUrl}
            imageWidth={currentPractice.imageWidth}
            imageHeight={currentPractice.imageHeight}
            hoveredId={hoveredId}
            onImageHover={showTextual ? undefined : handlePracticeImageHover}
            onImageClick={showTextual ? undefined : handlePracticeImageClick}
            segments={showTextual ? [] : currentPractice.segments}
            hideOverlay={showTextual || showVisual}
          />
          <CaptionText
            tokens={currentPractice.tokens}
            entities={currentPractice.segments}
            onCaptionHover={handlePracticeCaptionHover}
            onCaptionClick={handlePracticeCaptionClick}
            hoveredId={hoveredId}
            hoveredTokenId={hoveredTokenId}
            requireSegmentForInteraction={showSimpleVisual}
          />
        </div>
        {(showTextual || showVisual || showComplementary) && (
          <div style={styles.practiceExplanationWrap}>{renderPracticeExplanationPanel()}</div>
        )}
      </div>
    );
  };

  return (
    <div style={styles.root}>
      <div style={{ ...styles.card, ...((page >= 3 && page <= 5) ? styles.practiceCard : {}) }}>
        {/* Progress indicator */}
        <div style={styles.progress}>
          Step {page + 1} of {totalPages}
        </div>

        {/* ================================================================ */}
        {/* Page 0: Study context — Marie & James */}
        {/* ================================================================ */}
        {page === 0 && (
          <>
            <h1 style={styles.h1}>Help Build Marie And James' Travel Photobook</h1>
            <div style={styles.context}>
              <div style={styles.characterRow}>
                {TUTORIAL_INTRO_PEOPLE.map((person) => (
                  <div style={styles.avatar} key={person.name}>
                    <img
                      src={getOptimizedImageUrl(person.image)}
                      alt={person.name}
                      style={styles.avatarImage}
                    />
                    <strong>{person.name}</strong>
                  </div>
                ))}
              </div>

              <p style={{ marginTop: 16 }}>
                Imagine you are a close friend of <strong>Marie</strong> and <strong>James</strong>.
                They went on three trips together and want to create a photobook from
                their travel photos. An AI tool has generated captions for the photos,
                and your task is to help decide whether each caption is accurate,
                specific, and useful enough for the photobook.
              </p>

              <div style={styles.tripList}>
                <div style={styles.tripItem}>
                  <span style={styles.tripIcon}>🌴</span>
                  <div>
                    <strong>Tenerife Trip:</strong> sun, beaches, restaurants, and Loro Parque
                  </div>
                </div>
                <div style={styles.tripItem}>
                  <span style={styles.tripIcon}>🏔️</span>
                  <div>
                    <strong>Hiking Trip in Austria:</strong> alpine trails with Marie, James, and Daniel
                  </div>
                </div>
                <div style={styles.tripItem}>
                  <span style={styles.tripIcon}>🏞️</span>
                  <div>
                    <strong>Norway Trip:</strong> Fjords, nature and northern adventures
                  </div>
                </div>
              </div>

              <div style={styles.taskCallout}>
                <div style={styles.taskCalloutLabel}>Your task</div>
                <div style={styles.taskCalloutText}>
                  Look carefully at each image and caption. Decide how good the
                  caption would be for their photobook.
                </div>
              </div>
            </div>
          </>
        )}

        {/* ================================================================ */}
        {/* Page 1: Caption quality scale */}
        {/* ================================================================ */}
        {page === 1 && (
          <>
            <h1 style={styles.h1}>How To Rate Each Caption</h1>
            <p style={{ marginBottom: 16 }}>
              A good caption should be correct, specific, and useful for remembering
              the travel moment. Use this 5-point scale:
            </p>
            <div style={styles.scaleList}>
              {RATING_SCALE.map((item) => (
                <div key={item.rating} style={styles.scaleItem}>
                  <div style={styles.scaleBadge}>{item.rating}</div>
                  <div>
                    <strong>{item.label}:</strong> {item.desc}
                  </div>
                </div>
              ))}
            </div>
          </>
        )}

        {/* ================================================================ */}
        {/* Page 2: How to interact (no condition name) */}
        {/* ================================================================ */}
        {page === 2 && (
          <>
            <h1 style={styles.h1}>How To Explore The Images</h1>

            {isNoExplanation ? (
              <div style={styles.context}>
                <p>
                  For each photo, you will see the image and its AI-generated
                  caption below it. Take your time to examine both, then rate
                  the caption quality.
                </p>
                <div style={styles.interactionBox}>
                  <strong>Your workflow:</strong>
                  <ol style={{ margin: "8px 0 0", paddingLeft: 20 }}>
                    <li>Look at the image carefully</li>
                    <li>Read the generated caption</li>
                    <li>Rate the caption on the 5-point scale</li>
                  </ol>
                </div>
              </div>
            ) : (
              <div style={styles.context}>
                <p>
                  For each photo, you will see the image and its AI-generated
                  caption. Some caption words may have interactive support that links
                  them to image regions or short explanations:
                </p>

                {/* Feature: token highlighting */}
                {hasSegmentHighlight && (
                  <div style={styles.featureCard}>
                    <div style={styles.featureIcon}>🔤</div>
                    <div>
                      <strong>Caption words</strong>
                      <p style={{ margin: "4px 0 0", fontSize: 14 }}>
                        Some words in the caption are{" "}
                        <span style={styles.tokenDemo}>highlighted</span>.
                        These are words the AI linked to specific parts of the
                        image. Hover over them to see which region the AI
                        associated with that word.
                      </p>
                    </div>
                  </div>
                )}

                <div style={styles.featureCard}>
                  <div style={styles.featureIcon}>◉</div>
                  <div>
                    <strong>Concept support</strong>
                    <p style={{ margin: "4px 0 0", fontSize: 14 }}>
                      The AI links parts of the caption to concepts it thinks it
                      can recognize, such as a person, a place, an object, an
                      action, or a visible scene detail. Treat these links as
                      evidence to inspect, not as automatic proof that the
                      caption is correct.
                    </p>
                  </div>
                </div>

                {/* Feature: visual segment overlay */}
                {hasVisual && (
                  <div style={styles.featureCard}>
                    <div style={styles.featureIcon}>🖼️</div>
                    <div>
                      <strong>Image region highlighting</strong>
                      <p style={{ margin: "4px 0 0", fontSize: 14 }}>
                        When you hover over highlighted caption words or image
                        regions, coloured overlays appear on the image showing
                        which parts the AI focused on. Smaller regions
                        (e.g. a face) are highlighted on top of larger ones
                        (e.g. a body).
                      </p>
                    </div>
                  </div>
                )}

                {/* Feature: textual explanations */}
                {hasTextual && (
                  <div style={styles.featureCard}>
                    <div style={styles.featureIcon}>💬</div>
                    <div>
                      <strong>Text explanations</strong>
                      <p style={{ margin: "4px 0 0", fontSize: 14 }}>
                        When you hover over caption words or image regions, a
                        text explanation appears describing why the AI generated
                        that part of the caption.
                      </p>
                    </div>
                  </div>
                )}

                <div style={{ ...styles.interactionBox, marginTop: 16 }}>
                  <strong>On the next screens:</strong>
                  <p style={{ margin: "8px 0 0" }}>
                    The practice images and main study use the same interaction pattern:
                    hover or click{" "}
                    <span style={styles.tokenDemo}>highlighted words</span>{" "}
                    in the caption or on image regions to explore the AI's
                    support for that caption part. A clicked caption word or
                    region stays selected until you click it again or choose
                    another one. You'll try this yourself on the practice images
                    that follow.
                  </p>
                </div>
              </div>
            )}
          </>
        )}

        {/* ================================================================ */}
        {/* Pages 3-5: Practice images from sample.json */}
        {/* ================================================================ */}
        {page >= 3 && page <= 5 && currentPractice && (
          <>
            <h1 style={styles.h1}>
              Practice Image {practiceIndex + 1} of {samples.length}
            </h1>
            <div style={styles.practiceLayout}>
              {renderPracticeStudyPreview()}

              {!showFeedback ? (
                <div style={styles.ratingSection}>
                  <h2 style={styles.ratingHeading}>
                    How well does the caption describe this image?
                  </h2>
                  <div
                    className="rating-options rating-options--horizontal"
                    role="radiogroup"
                    aria-label="Caption quality rating"
                  >
                    {RATING_SCALE.map(({ value, label, desc, color }) => {
                      const selected = practiceRating === value;
                      return (
                        <label
                          key={value}
                          className={`rating-card${selected ? " is-selected" : ""}`}
                          data-rating={value}
                          style={{ "--rating-accent": color }}
                        >
                          <input
                            type="radio"
                            name="practiceRating"
                            value={value}
                            checked={selected}
                            onChange={(e) => setPracticeRating(e.target.value)}
                          />
                          <span className="rating-card__badge">{value}</span>
                          <span className="rating-card__label">{label}</span>
                          <span className="rating-card__desc">{desc}</span>
                        </label>
                      );
                    })}
                  </div>
                  <Button
                    variant="contained"
                    disabled={!practiceRating}
                    onClick={handleRateSubmit}
                    sx={{ mt: 2 }}
                  >
                    Submit Rating
                  </Button>
                </div>
              ) : (
                <div
                  style={{
                    ...styles.feedbackBox,
                    borderColor:
                      ratingAccepted
                        ? "#16a34a"
                        : "#ea580c",
                    backgroundColor:
                      ratingAccepted
                        ? "#f0fdf4"
                        : "#fff7ed",
                  }}
                >
                  <p style={{ fontWeight: 600, marginBottom: 4 }}>
                    {ratingAccepted
                      ? "Good choice."
                      : `A closer rating would be ${currentPractice.expectedRating}.`}
                  </p>
                  <p>
                    {currentPractice.feedback?.[practiceRating] ||
                      "Use the scale to decide how accurate and useful the caption would be for the photobook."}
                  </p>
                  <Button
                    variant="contained"
                    onClick={handleNextFromFeedback}
                    sx={{ mt: 2 }}
                  >
                    {practiceIndex < samples.length - 1
                      ? "Next Practice Image"
                      : "Continue"}
                  </Button>
                </div>
              )}
            </div>
          </>
        )}

        {/* ================================================================ */}
        {/* Page 6: Ready */}
        {/* ================================================================ */}
        {page === 6 && (
          <>
            <h1 style={styles.h1}>Your First Trip Starts Now</h1>
            <p style={{ fontSize: 16, lineHeight: 1.6, marginBottom: 16 }}>
              You are ready to help Marie and James check their photobook captions. Remember:
            </p>
            <ul style={{ fontSize: 15, lineHeight: 1.8, marginBottom: 20 }}>
              <li>View the image and read the generated caption</li>
              <li>Rate the caption quality on the 5-point scale</li>
              {hasSegmentHighlight && (
                <li>Explore highlighted words and image regions</li>
              )}
            </ul>
            <p style={{ fontSize: 16, lineHeight: 1.6, marginBottom: 16 }}>
              The main study starts with the Tenerife trip and then continues through Austria and Norway. There are <strong>3 trips</strong>, each with{" "}
              <strong>10 images</strong>. After each image you will rate the
              caption quality, and after each trip you will answer a short
              questionnaire.
            </p>
            <Button
              variant="contained"
              size="large"
              onClick={onComplete}
              sx={{
                background:
                  "linear-gradient(135deg, #16a34a 0%, #15803d 100%)",
                "&:hover": {
                  background:
                    "linear-gradient(135deg, #15803d 0%, #166534 100%)",
                },
                mt: 2,
              }}
            >
              Start the Study
            </Button>
          </>
        )}

        {/* Navigation (not on practice pages or final page) */}
        {page < 3 && (
          <div style={styles.nav}>
            {page > 0 && (
              <Button variant="outlined" onClick={() => setPage((p) => p - 1)}>
                Back
              </Button>
            )}
            <Button variant="contained" onClick={() => setPage((p) => p + 1)}>
              Next
            </Button>
          </div>
        )}
      </div>
    </div>
  );
}

const styles = {
  root: {
    minHeight: "100vh",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    backgroundColor: "#f8fafc",
    padding: 20,
  },
  card: {
    maxWidth: 820,
    width: "100%",
    backgroundColor: "#fff",
    borderRadius: 12,
    padding: "32px 40px",
    boxShadow: "0 4px 24px rgba(0,0,0,0.08)",
  },
  practiceCard: {
    maxWidth: "min(96vw, 1480px)",
    padding: "28px clamp(20px, 3vw, 36px)",
  },
  progress: {
    fontSize: 13,
    color: "#64748b",
    marginBottom: 8,
    fontWeight: 500,
  },
  h1: { fontSize: 24, marginBottom: 16, color: "#1e293b" },
  context: { fontSize: 16, lineHeight: 1.7, color: "#334155" },
  // Character intro
  characterRow: {
    display: "flex",
    gap: 48,
    justifyContent: "center",
    marginBottom: 8,
  },
  avatar: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    gap: 6,
    fontSize: 15,
    color: "#1e293b",
  },
  avatarImage: {
    width: 132,
    height: 132,
    objectFit: "cover",
    borderRadius: "50%",
    border: "3px solid #bfdbfe",
  },
  // Trip list
  tripList: {
    display: "flex",
    flexDirection: "column",
    gap: 10,
    marginTop: 12,
    marginBottom: 4,
  },
  tripItem: {
    display: "flex",
    alignItems: "center",
    gap: 12,
    padding: "10px 14px",
    backgroundColor: "#f8fafc",
    borderRadius: 8,
    border: "1px solid #e2e8f0",
    fontSize: 15,
  },
  tripIcon: { fontSize: 24, flexShrink: 0 },
  // Scale
  scaleList: { display: "flex", flexDirection: "column", gap: 12 },
  scaleReminder: {
    backgroundColor: "#f8fafc",
    border: "1px solid #e2e8f0",
    borderRadius: 10,
    padding: "10px 14px",
    marginBottom: 16,
  },
  scaleReminderSummary: {
    cursor: "pointer",
    fontWeight: 600,
    fontSize: 14,
    color: "#334155",
  },
  taskCallout: {
    marginTop: 18,
    background: "#eff6ff",
    border: "1px solid #bfdbfe",
    borderLeft: "5px solid #2563eb",
    borderRadius: 10,
    padding: "14px 18px",
  },
  taskCalloutLabel: {
    fontSize: 12,
    fontWeight: 700,
    letterSpacing: "0.08em",
    textTransform: "uppercase",
    color: "#2563eb",
    marginBottom: 4,
  },
  taskCalloutText: {
    fontSize: 17,
    fontWeight: 600,
    lineHeight: 1.5,
    color: "#1e293b",
  },
  scaleItem: {
    display: "flex",
    gap: 12,
    alignItems: "flex-start",
    fontSize: 14,
    lineHeight: 1.5,
    color: "#334155",
  },
  scaleBadge: {
    minWidth: 32,
    height: 32,
    borderRadius: "50%",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    backgroundColor: "#2563eb",
    color: "#fff",
    fontWeight: 700,
    fontSize: 15,
    flexShrink: 0,
  },
  // Interaction
  interactionBox: {
    backgroundColor: "#eff6ff",
    borderLeft: "4px solid #2563eb",
    borderRadius: 6,
    padding: "14px 18px",
    fontSize: 15,
    lineHeight: 1.5,
  },
  // Feature cards
  featureCard: {
    display: "flex",
    gap: 14,
    padding: "14px 16px",
    marginTop: 12,
    backgroundColor: "#f0fdf4",
    borderRadius: 8,
    border: "1px solid #bbf7d0",
  },
  featureIcon: { fontSize: 28, flexShrink: 0, marginTop: 2 },
  tokenDemo: {
    backgroundColor: "rgba(37, 99, 235, 0.12)",
    padding: "1px 5px",
    borderRadius: 4,
    borderBottom: "2px solid #2563eb",
    fontWeight: 600,
  },
  // Practice
  practiceLayout: { display: "flex", flexDirection: "column", gap: 16 },
  practiceStudyPreview: {
    display: "grid",
    gridTemplateColumns: "minmax(0, 1.55fr) minmax(0, 1fr)",
    gap: 24,
    alignItems: "start",
  },
  practicePreviewSingle: {
    minWidth: 0,
    width: "100%",
    maxWidth: "min(100%, 540px)",
    margin: "0 auto",
    display: "flex",
    flexDirection: "column",
    gap: 12,
  },
  practiceExplanationWrap: {
    minWidth: 0,
    maxHeight: "min(70vh, 620px)",
    overflow: "auto",
  },
  practiceImage: {
    width: "100%",
    maxHeight: 400,
    objectFit: "contain",
    borderRadius: 8,
    border: "1px solid #e2e8f0",
  },
  practiceCaption: {
    fontSize: 15,
    lineHeight: 1.6,
    padding: "10px 14px",
    backgroundColor: "#f1f5f9",
    borderRadius: 6,
  },
  ratingSection: {
    marginTop: 8,
    border: "1px solid #dbeafe",
    borderRadius: 8,
    padding: 14,
    backgroundColor: "#f8fafc",
  },
  ratingHeading: {
    fontSize: 18,
    color: "#1e293b",
    margin: "0 0 10px",
  },
  ratingGuide: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(160px, 1fr))",
    gap: 8,
    marginBottom: 12,
    color: "#475569",
    fontSize: 13,
    fontWeight: 600,
  },
  ratingRow: {
    display: "grid",
    gridTemplateColumns: "repeat(5, minmax(0, 1fr))",
    gap: 10,
  },
  ratingLabel: {
    position: "relative",
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    textAlign: "center",
    gap: 6,
    cursor: "pointer",
    fontSize: 14,
    border: "2px solid #e2e8f0",
    borderRadius: 14,
    padding: "12px 10px 14px",
    backgroundColor: "#fff",
    boxShadow: "0 1px 2px rgba(15, 23, 42, 0.04)",
    transition:
      "transform 0.15s ease, border-color 0.15s ease, box-shadow 0.15s ease",
  },
  ratingLabelSelected: {
    borderColor: "#2563eb",
    backgroundColor: "#eff6ff",
    boxShadow: "0 8px 20px -10px rgba(37, 99, 235, 0.6)",
  },
  ratingLabelHover: {
    borderColor: "#93c5fd",
    backgroundColor: "#f8fbff",
  },
  ratingBadge: {
    width: 36,
    height: 36,
    borderRadius: "50%",
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    fontWeight: 700,
    fontSize: 15,
    color: "#2563eb",
    backgroundColor: "rgba(37, 99, 235, 0.10)",
    border: "2px solid rgba(37, 99, 235, 0.30)",
  },
  ratingBadgeSelected: {
    backgroundColor: "#2563eb",
    color: "#fff",
    borderColor: "#2563eb",
  },
  ratingHiddenInput: {
    position: "absolute",
    opacity: 0,
    pointerEvents: "none",
    width: 0,
    height: 0,
  },
  ratingText: {
    color: "#334155",
    display: "flex",
    flexDirection: "column",
    gap: 2,
  },
  ratingTextLabel: {
    fontWeight: 700,
    color: "#1e293b",
    fontSize: 14,
  },
  ratingSummary: { color: "#64748b", fontSize: 12, lineHeight: 1.4 },
  feedbackBox: {
    border: "2px solid",
    borderRadius: 8,
    padding: "14px 18px",
    fontSize: 14,
    lineHeight: 1.6,
  },
  nav: {
    display: "flex",
    gap: 12,
    justifyContent: "flex-end",
    marginTop: 24,
  },
};
